import { Node, Edge } from "@xyflow/react";
import { NodeData } from "@zapier/canvas-state/src/types";
import {
  isDefaultNode,
  isZapNode,
  processNodesBeforeSave,
  processEdgesBeforeSave,
} from "@zapier/canvas-state";

import { toTemplatedString } from "../../../service/src/app/utils/stringUtils";

export function buildIdMapping<T extends { id: string }>(
  items: T[],
  prefix: string,
): Record<string, string> {
  return Object.fromEntries(
    items.map((item, index) => [item.id, `_TEMPLATE_${prefix}_${index + 1}`]),
  );
}

export function composeCanvasForTemplate(
  canvasName: string,
  nodes: Node<NodeData>[],
  edges: Edge[],
  resourceMap: { [p: string]: string },
) {
  const name = canvasName || "untitled";

  const newNodes: Node[] = nodes.map((node) => {
    const newNode: Node<NodeData> = {
      // Make a copy of the node, so we don't alter the existing canvas
      ...node,
      data: {
        ...node.data,
        // attributionSourceType needs to be last to overwrite original attributionSourceType
        attributionSourceType: "TEMPLATE",
      },
    };

    // Replace real asset IDs in node.data with templated strings from resourceMap
    if (isZapNode(newNode) && newNode.data.zapId) {
      newNode.data.zapId = toTemplatedString(resourceMap[newNode.data.zapId]);
    } else if (isDefaultNode(newNode)) {
      if (newNode.data.tableId) {
        newNode.data.tableId = toTemplatedString(
          resourceMap[newNode.data.tableId],
        );
      }
      if (newNode.data.agentId) {
        newNode.data.agentId = toTemplatedString(
          resourceMap[newNode.data.agentId],
        );
      }
      if (newNode.data.chatbotId) {
        newNode.data.chatbotId = toTemplatedString(
          resourceMap[newNode.data.chatbotId],
        );
      }
      if (newNode.data.projectId) {
        newNode.data.projectId = toTemplatedString(
          resourceMap[newNode.data.projectId],
        );
        if (newNode.data.pageId) {
          newNode.data.pageId = toTemplatedString(
            resourceMap[newNode.data.pageId],
          );
        }
      }
    }

    return newNode;
  });

  const newEdges: Edge[] = edges.map((edge) => ({
    // Make a copy of the edge, so we don't alter the existing canvas
    ...edge,
  }));

  // zsl-api only accepts BE nodes and edges
  const backendNodes = processNodesBeforeSave(newNodes);
  const backendEdges = processEdgesBeforeSave(newEdges);

  const nodesIdMapping = buildIdMapping(backendNodes, "NODE");
  const edgeIdMapping = buildIdMapping(backendEdges, "EDGE");

  const nodesWithTempIds = backendNodes.map((node) => {
    return {
      ...node,
      id: nodesIdMapping[node.id],
      ...(node.parent_id ? { parent_id: nodesIdMapping[node.parent_id] } : {}),
    };
  });

  const edgesWithTempIds = backendEdges.map((edge) => ({
    ...edge,
    id: edgeIdMapping[edge.id],
    source_id: nodesIdMapping[edge.source_id],
    target_id: nodesIdMapping[edge.target_id],
  }));

  return {
    id: "_CANVAS_1",
    name,
    nodes: nodesWithTempIds,
    edges: edgesWithTempIds,
  };
}
